import { v1 as uuid } from 'uuid';
import React, { useState } from 'react';
import { ClinicFormProps } from '../../../CreateClinic/types';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { ClinicCondition } from '../../../types';
import useSetEditedCondition from './useSetEditedCondition';
import styled from 'styled-components';
import SelectClinicConditionDialog from './SelectClinicConditionDialog';
import { HealtheraButton, SecondaryButton } from '../../../../../healtheraComponents';

interface AddConditionButtonProps extends ClinicFormProps {
  isVisible: boolean;
  clinicId: string;
}

const AddConditionButton = ({ form, isVisible, clinicId }: AddConditionButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSelectClinicOpen, setIsSelectClinicOpen] = useState(false);
  const handleAddCondition = useAddCondition(form);

  if (!isVisible) {
    return null;
  }

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const openSelectClinic = () => {
    closeModal();
    setIsSelectClinicOpen(true);
  };
  const closeSelectClinic = () => setIsSelectClinicOpen(false);

  const handleCreateNew = () => {
    closeModal();
    handleAddCondition();
  };

  const handleSelectCondition = (condition: ClinicCondition) => {
    const newCondition = {
      ...createEmptyCondition(),
      ...condition,
      clinic_id: condition.clinic_id
    };
    const updatedConditions = getExtendedConditions(form, newCondition);
    form.setValue('clinic_conditions', updatedConditions);
  };

  return (
    <>
      <HealtheraButton onClick={openModal}>
        Add condition
      </HealtheraButton>

      <Dialog open={isModalOpen} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>Add Condition</DialogTitle>
        <DialogContent>
          <StyledDialogContent>
            <HealtheraButton width="100%" height="60px" onClick={handleCreateNew}>
              Create new condition
            </HealtheraButton>
            <HealtheraButton width="100%" height="60px" onClick={openSelectClinic}>
              Add existing condition from another clinic
            </HealtheraButton>
          </StyledDialogContent>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={closeModal}>Cancel</SecondaryButton>
        </DialogActions>
      </Dialog>

      <SelectClinicConditionDialog
        open={isSelectClinicOpen}
        onClose={closeSelectClinic}
        currentClinicId={clinicId}
        onSelect={handleSelectCondition}
        form={form}
      />
    </>
  );
};

const useAddCondition = (form: ClinicFormProps['form']) => {
  const setEditedCondition = useSetEditedCondition();

  const handleAddCondition = () => {
    const newCondition = createEmptyCondition();
    const updatedConditions = getExtendedConditions(form, newCondition);

    form.setValue('clinic_conditions', updatedConditions);
    setEditedCondition(newCondition._id);
  };

  return handleAddCondition;
};

function getExtendedConditions(form: ClinicFormProps['form'], newCondition: ClinicCondition) {
  const conditions = form.getValues('clinic_conditions');
  return [...conditions, newCondition];
}

function createEmptyCondition(): ClinicCondition {
  return {
    _id: uuid(),
    condition_id: '',
    questionnaire_id: null,
    returning_questionnaire_id: null,
    is_video_call_required: false,
  };
}

export default AddConditionButton;

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
`;
